import { CpvPrefix, WatchlistRules } from '@elsa/contracts';
import {
  Badge,
  Button,
  Card,
  ErrorBox,
  Field,
  Input,
  Link,
  Loading,
  Note,
  Page,
  Select,
  Table,
  Td,
  useSearch,
} from '@elsa/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { fmtDate, fmtDay, fmtLei } from '../lib/format.ts';
import { useOrg } from '../lib/session.tsx';
import { app, friendly, must, rpc } from '../lib/supabase.ts';
import type { AcqRow, Watchlist } from '../lib/types.ts';
import { useFilterOptions } from './common.tsx';

// Alertele (docs/22 §2): reguli peste achizitiile noi. La 15 minute, achizitiile vazute prima
// data (cu o ora intarziere, ca produsele sa fie extrase) devin notificari; dimineata pleaca
// digestul pe email. Personale (doar tu) sau comune (toata firma; cer alerts.team).

const list = (s: string) =>
  s
    .split(/[,;\n]/)
    .map((x) => x.trim())
    .filter(Boolean);

function describe(r: Record<string, unknown>, cats: Map<number, string>): string {
  const parts: string[] = [];
  const arr = (k: string) => (Array.isArray(r[k]) ? (r[k] as unknown[]) : []);
  if (arr('keywords').length) parts.push(`cuvinte: ${arr('keywords').join(', ')}`);
  if (arr('cpv_prefixes').length) parts.push(`CPV: ${arr('cpv_prefixes').join(', ')}`);
  if (arr('categories').length)
    parts.push(
      `categorii: ${arr('categories')
        .map((c) => cats.get(Number(c)) ?? c)
        .join(', ')}`,
    );
  if (arr('counties').length) parts.push(`județe: ${arr('counties').join(', ')}`);
  if (r.authority_id) parts.push(`autoritatea ${r.authority_id}`);
  if (r.value_min !== undefined) parts.push(`≥ ${fmtLei(r.value_min as number)}`);
  if (r.value_max !== undefined) parts.push(`≤ ${fmtLei(r.value_max as number)}`);
  return parts.join(' · ');
}

function NewAlert({ initial, onDone }: { initial: Record<string, unknown>; onDone(): void }) {
  const { org, can, session } = useOrg();
  const opts = useFilterOptions();
  const qc = useQueryClient();
  const [rules, setRules] = useState<Record<string, unknown> | null>(null);
  const [error, setError] = useState<unknown>(null);
  const preview = useQuery({
    queryKey: ['watchlist_preview', rules],
    enabled: Boolean(rules),
    queryFn: () => rpc<AcqRow[]>('watchlist_preview', { p_rules: rules }),
  });
  const save = useMutation({
    mutationFn: async (w: {
      name: string;
      scope: string;
      notify_email: boolean;
      rules: Record<string, unknown>;
    }) =>
      must(
        app()
          .from('watchlists')
          .insert({ organization_id: org.id, created_by: session.user.id, ...w }),
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['watchlists'] });
      onDone();
    },
    onError: (e) => setError(new Error(friendly((e as Error).message))),
  });

  const read = (f: FormData) => {
    const cats = f.getAll('categories').map(Number);
    const counties = f.getAll('counties').map(String).filter(Boolean);
    const num = (k: string) => {
      const v = String(f.get(k) ?? '')
        .trim()
        .replace(',', '.');
      return v ? Number(v) : undefined;
    };
    const raw: Record<string, unknown> = {
      keywords: list(String(f.get('keywords') ?? '')),
      cpv_prefixes: list(String(f.get('cpv') ?? '')),
      categories: cats,
      counties,
      ...(initial.authority_id ? { authority_id: initial.authority_id } : {}),
      value_min: num('value_min'),
      value_max: num('value_max'),
    };
    for (const k of Object.keys(raw)) {
      const v = raw[k];
      if (v === undefined || (Array.isArray(v) && !v.length)) delete raw[k];
    }
    for (const c of (raw.cpv_prefixes as string[] | undefined) ?? []) {
      if (!CpvPrefix.safeParse(c).success) throw new Error(`Cod CPV invalid: ${c}`);
    }
    const r = WatchlistRules.safeParse(raw);
    if (!r.success) throw new Error(r.error.issues[0]?.message ?? 'Reguli invalide');
    return r.data as Record<string, unknown>;
  };

  const form = useRef<HTMLFormElement>(null);
  const submit = (mode: 'preview' | 'save') => {
    if (!form.current) return;
    const f = new FormData(form.current);
    setError(null);
    try {
      const r = read(f);
      if (mode === 'preview') setRules(r);
      else
        save.mutate({
          name: String(f.get('name')).trim(),
          scope: String(f.get('scope') ?? 'personal'),
          notify_email: f.get('notify_email') === 'on',
          rules: r,
        });
    } catch (err) {
      setError(err);
    }
  };
  const arr = (k: string) =>
    Array.isArray(initial[k]) ? (initial[k] as unknown[]).map(String) : [];
  return (
    <Card title="Alertă nouă">
      <form
        ref={form}
        className="space-y-3"
        onSubmit={(e) => {
          e.preventDefault();
          submit('save');
        }}
      >
        <Field label="Numele alertei">
          <Input
            name="name"
            required
            maxLength={120}
            defaultValue={arr('keywords')[0] ?? ''}
            placeholder="ex. Seringi în Cluj"
          />
        </Field>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Cuvinte cheie" help="Separate prin virgulă; oricare dintre ele.">
            <Input
              name="keywords"
              defaultValue={arr('keywords').join(', ')}
              placeholder="seringi, ace hipodermice"
            />
          </Field>
          <Field label="Coduri CPV (prefix)" help="Separate prin virgulă, ex. 3314, 33141310.">
            <Input name="cpv" defaultValue={arr('cpv_prefixes').join(', ')} inputMode="numeric" />
          </Field>
          <Field label="Valoarea minimă (lei)">
            <Input
              name="value_min"
              inputMode="decimal"
              defaultValue={initial.value_min ? String(initial.value_min) : ''}
            />
          </Field>
          <Field label="Valoarea maximă (lei)">
            <Input
              name="value_max"
              inputMode="decimal"
              defaultValue={initial.value_max ? String(initial.value_max) : ''}
            />
          </Field>
        </div>
        <fieldset className="space-y-1">
          <legend className="eticheta text-gri-700">Categorii (opțional)</legend>
          <div className="grid gap-1 sm:grid-cols-2">
            {opts.data?.categories.map((c) => (
              <label key={c.id} className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  name="categories"
                  value={c.id}
                  defaultChecked={arr('categories').includes(String(c.id))}
                />
                {c.name}
              </label>
            ))}
          </div>
        </fieldset>
        <Field label="Județe (opțional)" help="Țin apăsat Ctrl / ⌘ pentru mai multe.">
          <Select name="counties" multiple className="h-28" defaultValue={arr('counties')}>
            {opts.data?.counties.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </Select>
        </Field>
        {initial.authority_id ? (
          <Note>Doar achizițiile autorității {String(initial.authority_id)}.</Note>
        ) : null}
        <div className="flex flex-wrap items-center gap-4 text-sm">
          <label className="flex items-center gap-2">
            <input type="checkbox" name="notify_email" defaultChecked />
            Email zilnic
          </label>
          {can('alerts.team') ? (
            <Select
              name="scope"
              defaultValue="personal"
              className="w-auto"
              aria-label="Pentru cine"
            >
              <option value="personal">Doar pentru mine</option>
              <option value="team">Pentru toată firma</option>
            </Select>
          ) : null}
        </div>
        <ErrorBox error={error ?? preview.error} />
        <div className="flex flex-wrap gap-2">
          <Button type="button" onClick={() => submit('preview')}>
            Previzualizează (ultimele 30 de zile)
          </Button>
          <Button tone="primary" type="submit" disabled={save.isPending}>
            Salvează alerta
          </Button>
          <Button tone="ghost" onClick={onDone}>
            Renunță
          </Button>
        </div>
        {preview.isLoading && rules ? <Loading /> : null}
        {preview.data ? (
          <div className="space-y-1 text-sm">
            <div className="text-xs text-gri-700">
              {preview.data.length
                ? `${preview.data.length}${preview.data.length === 20 ? '+' : ''} potriviri în ultimele 30 de zile:`
                : 'Nicio potrivire în ultimele 30 de zile.'}
            </div>
            <ul className="space-y-0.5">
              {preview.data.map((a) => (
                <li key={a.id}>
                  <Link to={`/app/achizitii/${a.id}`} className="hover:underline">
                    {a.name}
                  </Link>{' '}
                  <span className="text-xs text-gri-700">
                    {fmtDay(a.finalization_day)} · {a.authority_name}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </form>
    </Card>
  );
}

export function AlertsPage() {
  const { org, can, session, entitlements } = useOrg();
  const qc = useQueryClient();
  const opts = useFilterOptions();
  const sp = useSearch();
  const initial = (() => {
    try {
      return JSON.parse(sp.get('reguli') ?? 'null') as Record<string, unknown> | null;
    } catch {
      return null;
    }
  })();
  const [adding, setAdding] = useState(Boolean(initial));
  const q = useQuery({
    queryKey: ['watchlists', org.id],
    queryFn: () =>
      must(
        app().from('watchlists').select('*').eq('organization_id', org.id).order('created_at'),
      ) as Promise<Watchlist[]>,
  });
  const update = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Partial<Watchlist> }) =>
      must(app().from('watchlists').update(patch).eq('id', id)),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['watchlists'] }),
  });
  const remove = useMutation({
    mutationFn: (id: string) => must(app().from('watchlists').delete().eq('id', id)),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['watchlists'] }),
  });
  const cats = new Map((opts.data?.categories ?? []).map((c) => [c.id, c.name]));
  const max = entitlements?.features.max_watchlists;
  const mine = (w: Watchlist) =>
    (w.scope === 'personal' && w.created_by === session.user.id) ||
    (w.scope === 'team' && can('alerts.team'));

  if (!can('alerts.own')) {
    return (
      <Page title="Alerte">
        <Note>
          Rolul tău în firmă (vizualizare) nu are alerte. Le vezi pe cele comune în Notificări.
        </Note>
      </Page>
    );
  }
  return (
    <Page
      title="Alerte"
      actions={
        adding ? null : (
          <Button tone="primary" onClick={() => setAdding(true)}>
            Alertă nouă
          </Button>
        )
      }
    >
      <p className="text-sm text-gri-800">
        Firma folosește {q.data?.length ?? 0}
        {typeof max === 'number' ? ` din ${max}` : ''} alerte. Achizițiile noi apar în Notificări în
        cel mult o oră și jumătate de la extragere; emailul pleacă dimineața, o dată pe zi.
      </p>
      {adding ? <NewAlert initial={initial ?? {}} onDone={() => setAdding(false)} /> : null}
      <ErrorBox error={q.error ?? update.error ?? remove.error} />
      <Card title="Alertele firmei">
        {q.isLoading ? (
          <Loading />
        ) : (
          <Table head={['Alerta', 'Pentru', 'Email', 'Activă', '']} empty={!q.data?.length}>
            {q.data?.map((w) => (
              <tr key={w.id}>
                <Td className="max-w-md">
                  <div className="font-medium">{w.name}</div>
                  <div className="text-xs text-gri-700">{describe(w.rules, cats)}</div>
                  <div className="text-xs text-gri-600">din {fmtDate(w.created_at)}</div>
                </Td>
                <Td>
                  {w.scope === 'team' ? <Badge tone="info">firmă</Badge> : <Badge>personală</Badge>}
                </Td>
                <Td>
                  <input
                    type="checkbox"
                    aria-label="Email zilnic"
                    checked={w.notify_email}
                    disabled={!mine(w)}
                    onChange={(e) =>
                      update.mutate({ id: w.id, patch: { notify_email: e.target.checked } })
                    }
                  />
                </Td>
                <Td>
                  <input
                    type="checkbox"
                    aria-label="Activă"
                    checked={w.enabled}
                    disabled={!mine(w)}
                    onChange={(e) =>
                      update.mutate({ id: w.id, patch: { enabled: e.target.checked } })
                    }
                  />
                </Td>
                <Td>
                  {mine(w) ? (
                    <Button
                      tone="danger"
                      onClick={() => {
                        if (confirm(`Ștergi alerta „${w.name}”?`)) remove.mutate(w.id);
                      }}
                    >
                      Șterge
                    </Button>
                  ) : null}
                </Td>
              </tr>
            ))}
          </Table>
        )}
      </Card>
    </Page>
  );
}
